import type { ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'

export type Tone =
  | 'blue' | 'green' | 'purple' | 'gray' | 'orange'
  | 'teal' | 'indigo' | 'amber' | 'red' | 'pink' | 'yellow'

const TONE_BG: Record<Tone, string> = {
  blue: 'bg-blue-50', green: 'bg-green-50', purple: 'bg-purple-50', gray: 'bg-gray-100',
  orange: 'bg-orange-50', teal: 'bg-teal-50', indigo: 'bg-indigo-50', amber: 'bg-amber-50',
  red: 'bg-red-50', pink: 'bg-pink-50', yellow: 'bg-yellow-50',
}
const TONE_ACCENT: Record<Tone, string> = {
  blue: 'bg-blue-500', green: 'bg-green-500', purple: 'bg-purple-500', gray: 'bg-gray-400',
  orange: 'bg-orange-500', teal: 'bg-teal-500', indigo: 'bg-indigo-500', amber: 'bg-amber-500',
  red: 'bg-red-500', pink: 'bg-pink-500', yellow: 'bg-yellow-500',
}
const TONE_ICON: Record<Tone, string> = {
  blue: 'bg-blue-100 text-blue-600', green: 'bg-green-100 text-green-600',
  purple: 'bg-purple-100 text-purple-600', gray: 'bg-gray-200 text-gray-600',
  orange: 'bg-orange-100 text-orange-600', teal: 'bg-teal-100 text-teal-600',
  indigo: 'bg-indigo-100 text-indigo-600', amber: 'bg-amber-100 text-amber-600',
  red: 'bg-red-100 text-red-600', pink: 'bg-pink-100 text-pink-600',
  yellow: 'bg-yellow-100 text-yellow-700',
}
const TONE_ACTIVE_RING: Record<Tone, string> = {
  blue: 'ring-blue-300 border-blue-300', green: 'ring-green-300 border-green-300',
  purple: 'ring-purple-300 border-purple-300', gray: 'ring-gray-300 border-gray-300',
  orange: 'ring-orange-300 border-orange-300', teal: 'ring-teal-300 border-teal-300',
  indigo: 'ring-indigo-300 border-indigo-300', amber: 'ring-amber-300 border-amber-300',
  red: 'ring-red-300 border-red-300', pink: 'ring-pink-300 border-pink-300',
  yellow: 'ring-yellow-300 border-yellow-300',
}

interface StatTileProps {
  label: string
  value: ReactNode
  tone?: Tone
  icon?: ReactNode
  tag?: { label: string; tone?: Tone }
  chevron?: boolean
  active?: boolean
  onClick?: () => void
  className?: string
}

/** A small colored stat tile: label, value, and either a top accent dash
 * (ResourceInn-style KPI tiles) or an icon square (row-card style), with
 * optional click-to-filter behavior. One shared shape used across the
 * employee Home page, the admin attendance roster filters, and the new HR
 * dashboard's stat-tile grids. */
export default function StatTile({
  label, value, tone = 'gray', icon, tag, chevron, active, onClick, className = '',
}: StatTileProps) {
  const Comp = onClick ? 'button' : 'div'
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-pressed={onClick ? !!active : undefined}
      title={onClick ? `Filter: ${label}` : undefined}
      className={`rounded-xl border p-3.5 flex flex-col gap-1.5 text-left transition-all ${TONE_BG[tone]} ${
        onClick ? 'hover:-translate-y-0.5 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40' : ''
      } ${active ? `${TONE_ACTIVE_RING[tone]} ring-2` : 'border-transparent'} ${className}`}
    >
      <div className="flex items-center justify-between gap-2">
        {icon ? (
          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${TONE_ICON[tone]}`}>
            {icon}
          </span>
        ) : (
          <span className={`h-1 w-5 rounded-full ${TONE_ACCENT[tone]}`} />
        )}
        {chevron && <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
      </div>
      <p className="text-[13px] font-semibold text-gray-700 leading-tight">{label}</p>
      <div className="flex items-center gap-2">
        <p className="text-lg sm:text-xl font-bold text-gray-900 tabular-nums">{value}</p>
        {tag && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${TONE_ICON[tag.tone || tone]}`}>
            {tag.label}
          </span>
        )}
      </div>
    </Comp>
  )
}
